import type {
  ApiResponse,
  BookingStats,
  EventStats,
  GroundStats,
  UserStats,
  DashboardStats,
  DashboardAnalytics,
  FanAnalytics,
  OrganizerAnalytics,
  ManagerAnalytics,
  AdminAnalytics,
  EventDemographics,
  SeatMapAnalytics,
  BookingTrend,
  AnalyticsQuery,
  TrackEventPayload,
  MonthlyStats,
} from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/analytics`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    "Analytics",
    "Dashboard",
    "UserStats",
    "BookingStats",
    "EventStats",
    "GroundStats",
    "RevenueStats",
    "Activity",
  ],
  endpoints: builder => ({
    // ============================================================================
    // ANALYTICS TRACKING
    // ============================================================================

    trackEvent: builder.mutation<ApiResponse<null>, TrackEventPayload>({
      query: data => ({
        url: "/track",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Analytics", "Activity"],
    }),

    // ============================================================================
    // DASHBOARD ENDPOINTS
    // ============================================================================

    getDashboardStats: builder.query<ApiResponse<DashboardStats>, void>({
      query: () => "/dashboard",
      providesTags: ["Dashboard"],
    }),

    getDashboardOverview: builder.query<ApiResponse<DashboardAnalytics>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/dashboard/overview",
        params,
      }),
      providesTags: ["Dashboard"],
    }),

    // ============================================================================
    // USER ANALYTICS
    // ============================================================================

    getUserStats: builder.query<ApiResponse<UserStats>, void>({
      query: () => "/users",
      providesTags: ["UserStats"],
    }),

    getUserAnalyticsById: builder.query<ApiResponse<any>, string>({
      query: userId => `/users/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: "UserStats", id: userId }],
    }),

    getUserEngagementStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/users/engagement",
        params,
      }),
      providesTags: ["UserStats"],
    }),

    // ============================================================================
    // BOOKING ANALYTICS
    // ============================================================================

    getBookingStats: builder.query<ApiResponse<BookingStats>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/bookings",
        params,
      }),
      providesTags: ["BookingStats"],
    }),

    getBookingAnalyticsById: builder.query<ApiResponse<any>, string>({
      query: bookingId => `/bookings/${bookingId}`,
      providesTags: (_result, _error, bookingId) => [{ type: "BookingStats", id: bookingId }],
    }),

    getBookingTrends: builder.query<ApiResponse<BookingTrend[]>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/bookings/trends",
        params,
      }),
      providesTags: ["BookingStats"],
    }),

    getBookingConversionStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/bookings/conversion",
        params,
      }),
      providesTags: ["BookingStats"],
    }),

    // ============================================================================
    // EVENT ANALYTICS
    // ============================================================================

    getEventStats: builder.query<ApiResponse<EventStats>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/events",
        params,
      }),
      providesTags: ["EventStats"],
    }),

    getEventAnalyticsById: builder.query<ApiResponse<any>, string>({
      query: eventId => `/events/${eventId}`,
      providesTags: (_result, _error, eventId) => [{ type: "EventStats", id: eventId }],
    }),

    getEventDemographics: builder.query<ApiResponse<EventDemographics>, string>({
      query: eventId => `/events/${eventId}/demographics`,
      providesTags: (_result, _error, eventId) => [{ type: "EventStats", id: eventId }],
    }),

    getEventSeatMapAnalytics: builder.query<ApiResponse<SeatMapAnalytics>, string>({
      query: eventId => `/events/${eventId}/seat-map`,
      providesTags: (_result, _error, eventId) => [{ type: "EventStats", id: eventId }],
    }),

    getEventPerformanceStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/events/performance",
        params,
      }),
      providesTags: ["EventStats"],
    }),

    // ============================================================================
    // GROUND ANALYTICS
    // ============================================================================

    getGroundStats: builder.query<ApiResponse<GroundStats>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/grounds",
        params,
      }),
      providesTags: ["GroundStats"],
    }),

    getGroundAnalyticsById: builder.query<ApiResponse<any>, string>({
      query: groundId => `/grounds/${groundId}`,
      providesTags: (_result, _error, groundId) => [{ type: "GroundStats", id: groundId }],
    }),

    getGroundUtilizationStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/grounds/utilization",
        params,
      }),
      providesTags: ["GroundStats"],
    }),

    getGroundRevenueStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/grounds/revenue",
        params,
      }),
      providesTags: ["GroundStats", "RevenueStats"],
    }),

    // ============================================================================
    // REVENUE ANALYTICS
    // ============================================================================

    getRevenueStats: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/revenue",
        params,
      }),
      providesTags: ["RevenueStats"],
    }),

    getRevenueTrends: builder.query<ApiResponse<MonthlyStats[]>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/revenue/trends",
        params,
      }),
      providesTags: ["RevenueStats"],
    }),

    // ============================================================================
    // ACTIVITY AND REPORTING
    // ============================================================================

    getRecentActivity: builder.query<ApiResponse<any[]>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/activities/recent",
        params,
      }),
      providesTags: ["Activity"],
    }),

    exportAnalyticsReport: builder.query<ApiResponse<any>, AnalyticsQuery>({
      query: (params = {}) => ({
        url: "/reports/export",
        params,
      }),
    }),

    // ============================================================================
    // ROLE-SPECIFIC ANALYTICS
    // ============================================================================

    getFanAnalytics: builder.query<ApiResponse<FanAnalytics>, void>({
      query: () => "/fan",
      providesTags: ["Analytics", "BookingStats"],
    }),

    getOrganizerAnalytics: builder.query<ApiResponse<OrganizerAnalytics>, void>({
      query: () => "/organizer",
      providesTags: ["Analytics", "EventStats", "RevenueStats"],
    }),

    getManagerAnalytics: builder.query<ApiResponse<ManagerAnalytics>, void>({
      query: () => "/manager",
      providesTags: ["Analytics", "GroundStats", "BookingStats"],
    }),

    getAdminAnalytics: builder.query<ApiResponse<AdminAnalytics>, void>({
      query: () => "/admin",
      providesTags: ["Analytics", "Dashboard", "UserStats", "EventStats", "GroundStats"],
    }),
  }),
});

export const {
  // Analytics Tracking
  useTrackEventMutation,

  // Dashboard
  useGetDashboardStatsQuery,
  useGetDashboardOverviewQuery,

  // User Analytics
  useGetUserStatsQuery,
  useGetUserAnalyticsByIdQuery,
  useGetUserEngagementStatsQuery,

  // Booking Analytics
  useGetBookingStatsQuery,
  useGetBookingAnalyticsByIdQuery,
  useGetBookingTrendsQuery,
  useGetBookingConversionStatsQuery,

  // Event Analytics
  useGetEventStatsQuery,
  useGetEventAnalyticsByIdQuery,
  useGetEventDemographicsQuery,
  useGetEventSeatMapAnalyticsQuery,
  useGetEventPerformanceStatsQuery,

  // Ground Analytics
  useGetGroundStatsQuery,
  useGetGroundAnalyticsByIdQuery,
  useGetGroundUtilizationStatsQuery,
  useGetGroundRevenueStatsQuery,

  // Revenue Analytics
  useGetRevenueStatsQuery,
  useGetRevenueTrendsQuery,

  // Activity and Reporting
  useGetRecentActivityQuery,
  useExportAnalyticsReportQuery,

  // Role-Specific Analytics
  useGetFanAnalyticsQuery,
  useGetOrganizerAnalyticsQuery,
  useGetManagerAnalyticsQuery,
  useGetAdminAnalyticsQuery,
} = analyticsApi;
